/** @format */

import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, ButtonBase, Chip, Skeleton } from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import KeyboardReturnRoundedIcon from "@mui/icons-material/KeyboardReturnRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ThumbUpAltRoundedIcon from "@mui/icons-material/ThumbUpAltRounded";
import WhatshotRoundedIcon from "@mui/icons-material/WhatshotRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import { fetchExamplePrompts } from "./propilotApi";
import { humanizeIntent } from "./UserAnalyticsCards";
import { deriveLifecycle, greetingFor, timeOfDay } from "./propilotEngagement";
import FactRail, { splitFactsByRail } from "./FactRails";

// Per-category visual identity (icon + accent). Keyed on the backend domain
// `key`. Unknown keys fall back to a neutral indigo insight icon so a new
// category added server-side still renders.
const CATEGORY_META = {
  ndr: { short: "NDR", icon: <ReportProblemRoundedIcon />, color: "#dc2626", bg: "#fef2f2" },
  sla: { short: "SLA", icon: <ScheduleRoundedIcon />, color: "#2563eb", bg: "#eff6ff" },
  rto: { short: "RTO", icon: <KeyboardReturnRoundedIcon />, color: "#d97706", bg: "#fffbeb" },
  carrier: { short: "Carrier", icon: <LocalShippingRoundedIcon />, color: "#059669", bg: "#ecfdf5" },
  pincode: { short: "Pincode", icon: <PublicRoundedIcon />, color: "#0891b2", bg: "#ecfeff" },
  payment: { short: "Payment", icon: <PaymentsRoundedIcon />, color: "#7c3aed", bg: "#f5f3ff" },
};
const DEFAULT_META = {
  short: "Insights",
  icon: <InsightsRoundedIcon />,
  color: "#6366f1",
  bg: "#eef2ff",
};

const metaFor = (key) => CATEGORY_META[key] || DEFAULT_META;

// Mirrors the backend /api/options/example-prompts shape so the screen is
// fully populated even when the fetch is in flight or fails.
const FALLBACK_DOMAINS = [
  {
    key: "ndr",
    label: "NDR (Non-Delivery Reports)",
    examples: [
      { text: "NDR rate by carrier for the last 30 days" },
      { text: "Top NDR reasons for the last 7 days" },
      { text: "Pending vs resolved NDR split by carrier this month" },
      { text: "NDR rate by zone for the last 14 days" },
    ],
  },
  {
    key: "sla",
    label: "SLA Performance",
    examples: [
      { text: "SLA breach % by zone for the last 30 days" },
      { text: "SLA breach % by carrier this month" },
      { text: "Average delivery days by zone for the last 30 days" },
      { text: "SLA breach % by payment mode for the last 30 days" },
    ],
  },
  {
    key: "rto",
    label: "RTO (Return to Origin)",
    examples: [
      { text: "RTO rate by carrier for the last 30 days" },
      { text: "Top RTO reasons by zone this month" },
      { text: "RTO rate by payment mode for the last 30 days" },
      { text: "RTO trend over the past 60 days" },
    ],
  },
  {
    key: "carrier",
    label: "Carrier Performance",
    examples: [
      { text: "Carrier performance comparison for the last 30 days" },
      { text: "Delivery rate by carrier this month" },
      { text: "Average shipping cost by carrier and zone for the last 30 days" },
      { text: "Shipment volume by carrier this week" },
    ],
  },
  {
    key: "pincode",
    label: "Pincode / Geography",
    examples: [
      { text: "Top 20 pincodes by RTO rate for the last 30 days (min 100 shipments)" },
      { text: "Top 10 cities by shipment volume last month" },
      { text: "Pincodes with highest NDR rate for the last 14 days" },
      { text: "Worst performing pincodes by delivery % this month" },
    ],
  },
  {
    key: "payment",
    label: "Payment & Returns",
    examples: [
      { text: "Prepaid vs COD split by carrier for the last 30 days" },
      { text: "COD shipment trend over the last 60 days" },
      { text: "Return reasons by carrier for the last 30 days" },
      { text: "Return rate by product category this month" },
    ],
  },
];

const firstNameOf = (name) => {
  if (!name) return "";
  return name.trim().split(/\s+/)[0];
};

// A category chip — clicking it swaps the visible example set.
const CategoryChip = ({ meta, active, onClick, disabled }) => (
  <Chip
    onClick={onClick}
    disabled={disabled}
    icon={React.cloneElement(meta.icon, {
      sx: { fontSize: "16px !important", color: `${active ? "#fff" : meta.color} !important` },
    })}
    label={meta.short}
    sx={{
      height: 30,
      fontSize: 12,
      fontWeight: 700,
      borderRadius: "999px",
      cursor: "pointer",
      px: 0.25,
      color: active ? "#fff" : "#475569",
      backgroundColor: active ? meta.color : "#fff",
      border: "1px solid",
      borderColor: active ? meta.color : "#e2e8f0",
      transition: "all 140ms ease",
      "& .MuiChip-label": { px: 0.75 },
      "&:hover": {
        backgroundColor: active ? meta.color : meta.bg,
        borderColor: meta.color,
      },
    }}
  />
);

// A single example question — full text, up to 2 lines, with a hover arrow.
const ExampleTile = ({ text, meta, onClick, disabled }) => (
  <ButtonBase
    onClick={() => onClick(text)}
    disabled={disabled}
    focusRipple
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      width: "100%",
      textAlign: "left",
      px: 1.4,
      py: 1,
      borderRadius: "12px",
      border: "1px solid #e2e8f0",
      backgroundColor: "#fff",
      transition: "all 160ms ease",
      "&:hover": {
        borderColor: meta.color,
        backgroundColor: meta.bg,
        transform: "translateY(-2px)",
        boxShadow: "0 8px 20px -10px rgba(15,23,42,0.25)",
      },
      "&.Mui-disabled": { opacity: 0.5 },
      "& .pp-arrow": { opacity: 0, transform: "translateX(-4px)" },
      "&:hover .pp-arrow": { opacity: 1, transform: "translateX(0)" },
    }}
  >
    <Box
      sx={{
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: "8px",
        backgroundColor: meta.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {React.cloneElement(meta.icon, { sx: { fontSize: 15, color: meta.color } })}
    </Box>
    <Typography
      sx={{
        flex: 1,
        minWidth: 0,
        fontSize: 12.5,
        fontWeight: 600,
        color: "#334155",
        lineHeight: 1.3,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }}
    >
      {text}
    </Typography>
    <ArrowForwardRoundedIcon
      className="pp-arrow"
      sx={{ fontSize: 15, color: meta.color, transition: "all 160ms ease", flexShrink: 0 }}
    />
  </ButtonBase>
);

// Compact one-row replacement for the old gradient analytics cards — keeps
// the numbers for returning users without eating the whole first fold.
const StatStrip = ({ stats, loading }) => {
  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        sx={{ width: "100%", maxWidth: 860, height: 36, borderRadius: "10px" }}
      />
    );
  }
  if (!stats) return null;

  const topTopic =
    stats.top_topics && stats.top_topics.length ? stats.top_topics[0] : null;

  const items = [
    {
      icon: <QuestionAnswerRoundedIcon />,
      color: "#6366f1",
      value: stats.total_questions ?? 0,
      label: "questions",
    },
    {
      icon: <CalendarMonthRoundedIcon />,
      color: "#059669",
      value: stats.questions_this_week ?? 0,
      label: "this week",
    },
    {
      icon: <ThumbUpAltRoundedIcon />,
      color: "#ea580c",
      value: stats.helpful_count ?? 0,
      label: "helpful",
    },
    topTopic && {
      icon: <WhatshotRoundedIcon />,
      color: "#9333ea",
      value: humanizeIntent(topTopic.intent),
      label: "top topic",
    },
  ].filter(Boolean);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 860,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: { xs: 1.5, md: 2.5 },
        rowGap: 0.5,
        px: 1.5,
        py: 0.75,
        borderRadius: "10px",
        backgroundColor: "#fff",
        border: "1px solid #e2e8f0",
      }}
    >
      {items.map((item, i) => (
        <Box
          key={i}
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}
        >
          {React.cloneElement(item.icon, {
            sx: { fontSize: 15, color: item.color },
          })}
          <Typography
            component="span"
            sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}
          >
            {item.value}
          </Typography>
          <Typography component="span" sx={{ fontSize: 12, color: "#64748b" }}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// Slim 3-step "how it works" strip shown only to first-time users — replaces
// the analytics (all zeros would just demotivate a brand-new user).
const ONBOARDING_STEPS = [
  {
    icon: <EditRoundedIcon />,
    color: "#6366f1",
    bg: "#eef2ff",
    title: "Ask in plain English",
    text: "e.g. “NDR rate by carrier for the last 30 days”",
  },
  {
    icon: <BarChartRoundedIcon />,
    color: "#059669",
    bg: "#ecfdf5",
    title: "Get an instant answer",
    text: "Numbers, charts or tables — whatever fits best",
  },
  {
    icon: <FileDownloadRoundedIcon />,
    color: "#d97706",
    bg: "#fffbeb",
    title: "Refine or export",
    text: "Follow up, dig deeper, or download the data",
  },
];

const OnboardingStrip = () => (
  <Box
    sx={{
      width: "100%",
      maxWidth: 860,
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
      gap: 1,
    }}
  >
    {ONBOARDING_STEPS.map((step, i) => (
      <Box
        key={i}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: 1.25,
          py: 1,
          borderRadius: "12px",
          backgroundColor: "#fff",
          border: "1px solid #e2e8f0",
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 26,
            height: 26,
            borderRadius: "8px",
            backgroundColor: step.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {React.cloneElement(step.icon, {
            sx: { fontSize: 15, color: step.color },
          })}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 12, fontWeight: 700, color: "#334155", lineHeight: 1.3 }}
          >
            {i + 1}. {step.title}
          </Typography>
          <Typography
            sx={{ fontSize: 11, color: "#64748b", lineHeight: 1.35, mt: 0.2 }}
          >
            {step.text}
          </Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      width: "100%",
      maxWidth: 860,
      fontSize: 10.5,
      fontWeight: 700,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      mb: 0.75,
      mt: 0.25,
    }}
  >
    {children}
  </Typography>
);

const EmptyState = ({
  onPromptClick,
  disabled,
  userName,
  analytics,
  analyticsLoading,
  merchantFacts,
}) => {
  const [domains, setDomains] = useState(FALLBACK_DOMAINS);
  const [activeKey, setActiveKey] = useState(FALLBACK_DOMAINS[0].key);

  useEffect(() => {
    let cancelled = false;
    fetchExamplePrompts().then((data) => {
      if (cancelled) return;
      if (data?.domains?.length) {
        setDomains(data.domains);
        setActiveKey((prev) =>
          data.domains.some((d) => d.key === prev) ? prev : data.domains[0].key
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const firstName = firstNameOf(userName);
  const stats = analytics?.stats || null;

  // Lifecycle-aware hero copy: first-timers get an onboarding pitch, returning
  // users get their numbers + a nudge toward the next milestone.
  const lifecycle = deriveLifecycle(stats);
  const topTopic =
    stats?.top_topics && stats.top_topics.length ? stats.top_topics[0] : null;
  // While analytics is in flight we don't yet know the stage — show a neutral
  // greeting instead of flashing "first time here?" at a returning user.
  const greeting =
    analyticsLoading && !stats
      ? {
          headline: `${timeOfDay(new Date().getHours())}${
            firstName ? `, ${firstName}` : ""
          } 👋`,
          subline:
            "ProPilot — your AI shipment analyst. Ask anything about your shipments in plain English.",
        }
      : greetingFor({
          lifecycle,
          firstName,
          hour: new Date().getHours(),
          topTopicLabel: topTopic ? humanizeIntent(topTopic.intent) : null,
        });
  const isNewUser = !analyticsLoading && lifecycle.stage === "new";

  const activeDomain = useMemo(
    () => domains.find((d) => d.key === activeKey) || domains[0],
    [domains, activeKey]
  );
  const activeMeta = metaFor(activeDomain?.key);
  // Cap at 4 so the grid stays 2 rows — keeps the landing scroll-free.
  const examples = (activeDomain?.examples || []).slice(0, 4);

  // "Did you know?" rails — performance facts left, network facts right.
  // Rendered only on lg+ (the rails need ~280px each); below that the
  // center column stands alone, exactly as before this feature.
  const railFacts = splitFactsByRail(merchantFacts?.facts);
  const showRails =
    railFacts.performance.length > 0 || railFacts.network.length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: { xs: "stretch", lg: "center" },
        justifyContent: "center",
        gap: 2.5,
        minHeight: "100%",
        px: { xs: 2, md: 3 },
        py: { xs: 1.5, md: 2 },
        overflowY: "auto",
      }}
    >
      {showRails ? (
        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: 280,
            flexShrink: 0,
            alignSelf: "center",
          }}
        >
          <FactRail
            title="Did you know?"
            icon="💡"
            subtitle={`last ${merchantFacts?.window_days || 30} days`}
            facts={railFacts.performance}
            onAsk={onPromptClick}
          />
        </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 1 860px",
          minWidth: 0,
        }}
      >
      {/* ── Personalised hero ─────────────────────────────────────────────── */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 860,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 0.5,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "13px",
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 8px 20px -8px rgba(99,102,241,0.6)",
          }}
        >
          <AutoAwesomeRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 17, md: 19 },
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
            }}
          >
            {greeting.headline}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.1, lineHeight: 1.35 }}>
            {greeting.subline}
          </Typography>
        </Box>
      </Box>

      {/* ── Usage at a glance: onboarding for first-timers, stat strip after ── */}
      <Box sx={{ width: "100%", maxWidth: 860, mt: 1.5, mb: 0.5, display: "flex" }}>
        {isNewUser ? (
          <OnboardingStrip />
        ) : (
          <StatStrip stats={stats} loading={analyticsLoading} />
        )}
      </Box>

      {/* ── Sample questions: category chips + that category's examples ───── */}
      <SectionLabel>Try asking about…</SectionLabel>
      <Box
        sx={{
          width: "100%",
          maxWidth: 860,
          display: "flex",
          flexWrap: "wrap",
          gap: 0.75,
          mb: 1,
        }}
      >
        {domains.map((d) => (
          <CategoryChip
            key={d.key}
            meta={metaFor(d.key)}
            active={d.key === activeKey}
            onClick={() => setActiveKey(d.key)}
            disabled={disabled}
          />
        ))}
      </Box>
      <Box
        sx={{
          width: "100%",
          maxWidth: 860,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1,
          mb: 1.25,
        }}
      >
        {examples.map((ex, i) => (
          <ExampleTile
            key={`${activeKey}-${i}`}
            text={ex.text}
            meta={activeMeta}
            onClick={onPromptClick}
            disabled={disabled}
          />
        ))}
      </Box>

      {/* ── Structure hint ───────────────────────────────────────────────── */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 860,
          px: 1.75,
          py: 0.85,
          borderRadius: "10px",
          backgroundColor: "#f8fafc",
          border: "1px dashed #cbd5e1",
          textAlign: "center",
        }}
      >
        <Typography sx={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.4 }}>
          💡 Tip — ask with{" "}
          <Box
            component="span"
            sx={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              color: "#4f46e5",
              fontWeight: 600,
            }}
          >
            [metric] by [dimension] for [time range]
          </Box>
          . Every follow-up filter, sort &amp; export is instant and free.
        </Typography>
      </Box>
      </Box>

      {showRails ? (
        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: 280,
            flexShrink: 0,
            alignSelf: "center",
          }}
        >
          <FactRail
            title="Your network"
            icon="📊"
            facts={railFacts.network}
            onAsk={onPromptClick}
            offsetMs={5000 /* flips 5s after the left rail, never together */}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default EmptyState;
